import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router';
import { FiberManualRecord } from '@material-ui/icons';

import { useProjectApi } from 'src/services';
import { TitleContainer, ContentContainer } from 'src/components';
import { AnalyticCard } from 'src/scenes/Project/components';
import { useFetchStatus } from 'src/hooks';


const useStyles = makeStyles(theme => ({
  grid: {
    height: '100%',
  },
  group: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray,
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
  sectionTitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  statusDot: {
    fontSize: '12px',
    marginRight: theme.spacing(1),
  },
  statusReady: {
    color: '#4CAF50', // Verde
  },
  statusNotReady: {
    color: '#9E9E9E', // Gris
  },
  statusNotImplemented: {
    color: '#BDBDBD', // Gris claro
  },
  itemTextNotImplemented: {
    color: '#BDBDBD',
    textDecoration: 'line-through',
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  nestedDeep: {
    paddingLeft: theme.spacing(6),
  },
}));

const ProgressStatusContainer = ({ project }) => {
  const classes = useStyles();

  const match = useRouteMatch();
  const projectApi = useProjectApi();
  const [ progressStatus, setProgressStatus ] = useState({});

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const { handleConfirm: fetchProgressStatus } = useFetchStatus({
    confirmFn: async () => {
      const progressStatus = await projectApi.getProjectProgressStatus(projectId);
      setProgressStatus(progressStatus);
    },
  });

  const renderStatusIcon = status => {
    let className = classes.statusNotImplemented;
    if (status === true) {
      className = classes.statusReady;
    } else if (status === false) {
      className = classes.statusNotReady;
    }
    return <FiberManualRecord className={`${classes.statusDot} ${className}`} />;
  };

  const renderItem = (label, status) => {
    const textClassName = status === undefined ? classes.itemTextNotImplemented : '';

    return (
      <Box key={label} display="flex" alignItems="center" py={0.5} mr={0.5}>
        {renderStatusIcon(status)}
        <Typography variant="body2" color="textPrimary" className={textClassName}>
          {label}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    fetchProgressStatus();
  }, [ projectId ]); // eslint-disable-line react-hooks/exhaustive-deps

  const { impactsAndDependencies, natureState, sites, naturalCapital, smaMonitoring } = progressStatus;

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={project}
        breadcrumbs={[ 'projects', 'project', { name: 'Estado de avance' } ]}
      >
        Estado de Avance
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Grid container spacing={2} direction="column">
          {!isSite &&
            <Grid item md={6} lg={4}>
              <AnalyticCard title="Impactos y dependencias">
                <Divider />
                <Box mt={2} />
                <Grid container spacing={1}>
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Visión general</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Métricas del proyecto', impactsAndDependencies?.overview?.projectMetrics)}
                      {renderItem('Área de interés - Mapa', impactsAndDependencies?.overview?.interestAreaMap)}
                      {renderItem('Clasificación ISIC', impactsAndDependencies?.overview?.isicClassification)}
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={1} />
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Presiones</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Screening', impactsAndDependencies?.pressures?.screening)}
                      {renderItem('Superficie convertida - Mapa', impactsAndDependencies?.pressures?.convertedSurfaceMap)}
                      {renderItem('Superficie convertida - Gráfico', impactsAndDependencies?.pressures?.convertedSurfaceChart)}
                      {renderItem('Huella espacial total', impactsAndDependencies?.pressures?.totalSpatialFootprint)}
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={1} />
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Dependencias</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Screening', impactsAndDependencies?.dependencies?.screening)}
                    </Box>
                  </Grid>
                </Grid>
              </AnalyticCard>
            </Grid>
          }
          <Grid item md={6} lg={4}>
            <AnalyticCard title={isSite ? 'Componentes' : 'Estado de la naturaleza'}>
              <Divider />
              <Box mt={2} />
              {!isSite &&
                <>
                  <Grid container spacing={1} justifyContent="center">
                    <Grid item xs={12} className={classes.grid}>
                      <Box className={classes.group}>
                        <Typography variant="body2" className={classes.sectionTitle}>Visión general</Typography>
                        <Divider />
                        <Box mt={1} />
                        {renderItem('Área de interés - Mapa', natureState?.overview?.interestAreaMap)}
                        {renderItem('Estado de la naturaleza - Nivel de preocupación', natureState?.overview?.natureStateNumber)}
                        {renderItem('Estado de la naturaleza - Gráfico araña', natureState?.overview?.natureStateSpiderChart)}
                      </Box>
                    </Grid>
                  </Grid>
                  <Box mt={1} />
                </>
              }
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.group}>
                    <Typography variant="body2" className={classes.sectionTitle}>Tierra</Typography>
                    <Divider />
                    <Box mt={1} />
                    {renderItem('Indicador PVN', natureState?.land?.pvnIndicator)}
                    {renderItem('NDVI - Mapa', natureState?.land?.spectralIndices?.ndviMap)}
                    {renderItem('NDVI - Gráfico', natureState?.land?.spectralIndices?.ndviChart)}
                    {renderItem('Albedo - Mapa', natureState?.land?.spectralIndices?.albedoMap)}
                    {renderItem('Albedo - Gráfico', natureState?.land?.spectralIndices?.albedoChart)}
                    {renderItem('NDMI - Mapa', natureState?.land?.spectralIndices?.ndmiMap)}
                    {renderItem('NDMI - Gráfico', natureState?.land?.spectralIndices?.ndmiChart)}
                    {renderItem('Landcover - Mapa', natureState?.land?.landUse?.landcoverMap)}
                    {renderItem('Landcover - Gráfico y tabla', natureState?.land?.landUse?.landcoverChartTable)}
                    {renderItem('Proporción vegetal natural - Mapa', natureState?.land?.landUse?.naturalVegProportionMap)}
                    {renderItem('Proporción vegetal natural - Gráfico', natureState?.land?.landUse?.naturalVegProportionChart)}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={1} />
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.group}>
                    <Typography variant="body2" className={classes.sectionTitle}>Biodiversidad</Typography>
                    <Divider />
                    <Box mt={1} />
                    {renderItem('Indicador EII', natureState?.biodiversity?.eiiIndicator)}
                    {renderItem('Indicador AEN', natureState?.biodiversity?.aenIndicator)}
                    {renderItem('EII - Mapa', natureState?.biodiversity?.ecosystemIntegrity?.eiiMap)}
                    {renderItem('EII reclasificado - Mapa', natureState?.biodiversity?.ecosystemIntegrity?.eiiReclassifiedMap)}
                    {renderItem('EII reclasificado - Gráfico', natureState?.biodiversity?.ecosystemIntegrity?.eiiReclassifiedChart)}
                    {renderItem('Ecosistemas - Tabla', natureState?.biodiversity?.ecosystemsTable)}
                    {renderItem('Evolución condición por ecosistema - Gráfico', natureState?.biodiversity?.ecosystemConditionEvolution)}
                    {renderItem('Riqueza - Mapa', natureState?.biodiversity?.integrityDetail?.richnessMap)}
                    {renderItem('Riqueza - Gráfico', natureState?.biodiversity?.integrityDetail?.richnessChart)}
                    {renderItem('Parches - Mapa', natureState?.biodiversity?.integrityDetail?.patchesMap)}
                    {renderItem('Parches - Tabla', natureState?.biodiversity?.integrityDetail?.patchesTable)}
                    {renderItem('GPP - Mapa', natureState?.biodiversity?.integrityDetail?.gppMap)}
                    {renderItem('GPP - Gráfico', natureState?.biodiversity?.integrityDetail?.gppChart)}
                    {renderItem('Lista EECC', natureState?.biodiversity?.eeccList)}
                    {renderItem('EECC Agrupados', natureState?.biodiversity?.eeccGrouped)}
                    {renderItem('Áreas protegidas', natureState?.biodiversity?.protectedAreas)}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={1} />
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.group}>
                    <Typography variant="body2" className={classes.sectionTitle}>Agua dulce</Typography>
                    <Divider />
                    <Box mt={1} />
                    {renderItem('Indicador CAD', natureState?.freshwater?.cadIndicator)}
                    {renderItem('Indicador DAD', natureState?.freshwater?.dadIndicator)}
                    {renderItem('NDTI - Mapa', natureState?.freshwater?.spectralIndices?.ndtiMap)}
                    {renderItem('NDTI - Gráfico', natureState?.freshwater?.spectralIndices?.ndtiChart)}
                    {renderItem('Cuerpo de agua - Mapa', natureState?.freshwater?.waterBodies?.ladcoverWaterMap)}
                    {renderItem('Cuerpo de agua - Gráfico', natureState?.freshwater?.waterBodies?.ladcoverWaterChart)}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={1} />
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.group}>
                    <Typography variant="body2" className={classes.sectionTitle}>Océano</Typography>
                    <Divider />
                    <Box mt={1} />
                    {renderItem('Sin implementar', undefined)}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={1} />
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.group}>
                    <Typography variant="body2" className={classes.sectionTitle}>Monitoreos SMA</Typography>
                    <Divider />
                    <Box mt={1} />
                    {renderItem('Lista de monitoreos', smaMonitoring?.smaMonitoring.monitoringList)}
                  </Box>
                </Grid>
              </Grid>
            </AnalyticCard>
          </Grid>
          {!isSite &&
            <Grid item md={6} lg={4}>
              <AnalyticCard title="Sitios">
                <Divider />
                <Box mt={2} />
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Visión general</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Localización de sitios - Mapa', sites?.overview?.sitesMap)}
                      {renderItem(
                        'Superficie - Número de sitios - Condición general - Proporción de vegetación → Indicadores',
                        sites?.overview?.indicators,
                      )}
                      {renderItem('Ecosistemas agregados de sitios - Tabla', sites?.overview?.ecosystemsTable)}
                      {renderItem('Resumen sitios - Tabla', sites?.overview?.sitesSummaryTable)}
                      {renderItem('Población de especies - Gráfico', sites?.overview?.speciesPopulationChart)}
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={1} />
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Sitios</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Métricas SMA sitios', sites?.sites?.smaSitesMetrics)}
                      {renderItem('Lista de sitio', sites?.sites?.sitesList)}
                    </Box>
                  </Grid>
                </Grid>
              </AnalyticCard>
            </Grid>
          }
          {!isSite &&
            <Grid item md={6} lg={4}>
              <AnalyticCard title="Capital natural">
                <Divider />
                <Box mt={2} />
                <Grid container spacing={1} justifyContent="center">
                  <Grid item xs={12} className={classes.grid}>
                    <Box className={classes.group}>
                      <Typography variant="body2" className={classes.sectionTitle}>Contabilidad física</Typography>
                      <Divider />
                      <Box mt={1} />
                      {renderItem('Activos por ecosistemas - Gráfico', naturalCapital?.physicalAccounting?.ecosystemChart)}
                      {renderItem('Inventario de activos por ecosistemas - Tabla', naturalCapital?.physicalAccounting?.ecosystemTable)}
                      {renderItem('Inventario de activos por especie - Tabla', naturalCapital?.physicalAccounting?.speciesTable)}
                      {renderItem('Activos por especies - Gráfico', naturalCapital?.physicalAccounting?.speciesChart)}
                    </Box>
                  </Grid>
                </Grid>
              </AnalyticCard>
            </Grid>
          }
        </Grid>
      </ContentContainer>
    </Grid>
  );
};

ProgressStatusContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  ProgressStatusContainer,
};
